// Message Payload Templates for Manual Trigger UI
// All 24 ESS Message Types with pre-configured templates
// Corrected based on ESS_API_Documentation.txt (Version 1.0.11, May 2025)

const generateRandomId = () => Math.floor(Math.random() * 100000);
const getCurrentTimestamp = () => {
  // ESS expects format: 2025-12-29T18:55:30.511 (no Z timezone indicator)
  const now = new Date();
  return now.toISOString().replace('Z', '');
};
// ESS requires full dateTime format for date fields (ValidityDate, MaturityDate, etc.)
const getCurrentDate = () => new Date().toISOString().replace('Z', '');

export const MESSAGE_TEMPLATES = {
  // ==================== CATEGORY 1: Disbursement & Approval ====================
  
  LOAN_DISBURSEMENT_NOTIFICATION: {
    type: 'LOAN_DISBURSEMENT_NOTIFICATION',
    displayName: 'Loan Disbursement',
    description: 'Notify successful loan disbursement',
    endpoint: '/messages/request',
    category: 'Disbursement & Approval',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_DISBURSEMENT_NOTIFICATION',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><Reason>Disbursement successful</Reason><FSPReferenceNumber>FSP{{RANDOM}}</FSPReferenceNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><TotalAmountToPay>5000.00</TotalAmountToPay><DisbursementDate>{{NOW}}</DisbursementDate>'
    }
  },

  LOAN_DISBURSEMENT_FAILURE_NOTIFICATION: {
    type: 'LOAN_DISBURSEMENT_FAILURE_NOTIFICATION',
    displayName: 'Disbursement Failure',
    description: 'Notify loan disbursement failure',
    endpoint: '/messages/request',
    category: 'Disbursement & Approval',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_DISBURSEMENT_FAILURE_NOTIFICATION',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><Reason>Insufficient funds in account</Reason>'
    }
  },

  LOAN_INITIAL_APPROVAL_NOTIFICATION: {
    type: 'LOAN_INITIAL_APPROVAL_NOTIFICATION',
    displayName: 'Loan Approval',
    description: 'Notify initial loan approval from FSP',
    endpoint: '/messages/request',
    category: 'Disbursement & Approval',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_INITIAL_APPROVAL_NOTIFICATION',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><Reason>Loan approved by FSP</Reason><FSPReferenceNumber>FSP{{RANDOM}}</FSPReferenceNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><TotalAmountToPay>5000.00</TotalAmountToPay><OtherCharges>100.00</OtherCharges><Approval>APPROVED</Approval>'
    }
  },

  LOAN_LIQUIDATION_NOTIFICATION: {
    type: 'LOAN_LIQUIDATION_NOTIFICATION',
    displayName: 'Loan Liquidation',
    description: 'Notify loan liquidation/full closure',
    endpoint: '/messages/request',
    category: 'Disbursement & Approval',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_LIQUIDATION_NOTIFICATION',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><Remarks>Loan fully repaid and closed</Remarks>'
    }
  },

  LOAN_STATUS_RESPONSE: {
    type: 'RESPONSE',
    displayName: 'Loan Status Response',
    description: 'Response to loan status request (uses generic RESPONSE type)',
    endpoint: '/messages/request',
    category: 'Disbursement & Approval',
    useGenericEndpoint: true,
    note: 'Documentation shows this uses generic RESPONSE message type',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'RESPONSE',
      MessageDetails: '<ResponseCode>8000</ResponseCode><Description>Loan is active with balance 3500.00</Description>'
    }
  },

  // ==================== CATEGORY 2: Repayment ====================
  
  FULL_LOAN_REPAYMENT_NOTIFICATION: {
    type: 'FULL_LOAN_REPAYMENT_NOTIFICATION',
    displayName: 'Full Repayment',
    description: 'Notify complete loan repayment',
    endpoint: '/messages/request',
    category: 'Repayment',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'FULL_LOAN_REPAYMENT_NOTIFICATION',
      MessageDetails: '<CheckNumber>111222333</CheckNumber><ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><PaymentReference>PAY{{RANDOM}}</PaymentReference><DeductionCode>DC001</DeductionCode><PaymentDescription>Full loan repayment</PaymentDescription><PaymentDate>{{NOW}}</PaymentDate><PaymentAmount>5000.00</PaymentAmount><LoanBalance>0.00</LoanBalance>'
    }
  },

  FULL_REPAYMENT_OFF_BALANCE_RESPONSE: {
    type: 'FULL_REPAYMENT_OFF_BALANCE_RESPONSE',
    displayName: 'Full Off-Balance Response',
    description: 'Response for full payoff balance request',
    endpoint: '/messages/request',
    category: 'Repayment',
    useGenericEndpoint: true,
    note: 'Fixed typo: was "0FF" now "OFF"',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'FULL_REPAYMENT_OFF_BALANCE_RESPONSE',
      MessageDetails: '<TotalPayOffAmount>5000.00</TotalPayOffAmount><LoanNumber>LOAN{{RANDOM}}</LoanNumber><LastDeductionDate>{{DATE}}</LastDeductionDate><FSPBankAccount>1234567890</FSPBankAccount><FSPBankAccountName>ZE DONE LOANS</FSPBankAccountName><SWIFTCode>ZDLTZ001</SWIFTCode><MNOChannels>MPESA,TIGOPESA</MNOChannels><PaymentReferenceNumber>PAY{{RANDOM}}</PaymentReferenceNumber><FinalPaymentDate>{{NOW}}</FinalPaymentDate><EndDate>{{DATE}}</EndDate>'
    }
  },

  PARTIAL_LOAN_REPAYMENT_NOTIFICATION: {
    type: 'PARTIAL_LOAN_REPAYMENT_NOTIFICATION',
    displayName: 'Partial Repayment',
    description: 'Notify partial loan repayment',
    endpoint: '/messages/request',
    category: 'Repayment',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'PARTIAL_LOAN_REPAYMENT_NOTIFICATION',
      MessageDetails: '<CheckNumber>111222333</CheckNumber><ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><PaymentReference>PAY{{RANDOM}}</PaymentReference><DeductionCode>DC001</DeductionCode><PaymentDescription>Partial payment installment</PaymentDescription><PaymentDate>{{NOW}}</PaymentDate><MaturityDate>{{DATE}}</MaturityDate><PaymentAmount>1000.00</PaymentAmount><LoanBalance>4000.00</LoanBalance>'
    }
  },

  PARTIAL_REPAYMENT_OFF_BALANCE_RESPONSE: {
    type: 'PARTIAL_REPAYMENT_OFF_BALANCE_RESPONSE',
    displayName: 'Partial Off-Balance Response',
    description: 'Response for partial payoff balance',
    endpoint: '/messages/request',
    category: 'Repayment',
    useGenericEndpoint: true,
    note: 'Fixed typo: was "0FF" now "OFF"',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'PARTIAL_REPAYMENT_OFF_BALANCE_RESPONSE',
      MessageDetails: '<TotalPayOffAmount>2500.00</TotalPayOffAmount><LoanNumber>LOAN{{RANDOM}}</LoanNumber><LastDeductionDate>{{DATE}}</LastDeductionDate><FSPBankAccount>1234567890</FSPBankAccount><FSPBankAccountName>ZE DONE LOANS</FSPBankAccountName><SWIFTCode>ZDLTZ001</SWIFTCode><MNOChannels>MPESA,TIGOPESA</MNOChannels><PaymentReferenceNumber>PAY{{RANDOM}}</PaymentReferenceNumber><FinalPaymentDate>{{NOW}}</FinalPaymentDate><EndDate>{{DATE}}</EndDate>'
    }
  },

  // ==================== CATEGORY 3: Restructuring ====================
  
  LOAN_RESTRUCTURE_REQUEST_FSP: {
    type: 'LOAN_RESTRUCTURE_REQUEST_FSP',
    displayName: 'Restructure Request (FSP)',
    description: 'FSP-initiated loan restructure request',
    endpoint: '/messages/request',
    category: 'Restructuring',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_RESTRUCTURE_REQUEST_FSP',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><InstallmentAmount>450.00</InstallmentAmount><OutstandingBalance>4500.00</OutstandingBalance><PrincipalBalance>4000.00</PrincipalBalance><ValidityDate>{{DATE}}</ValidityDate><LastRepaymentDate>{{DATE}}</LastRepaymentDate><MaturityDate>{{DATE}}</MaturityDate><Reason>FINANCIAL_HARDSHIP</Reason><NewInstallmentAmount>350.00</NewInstallmentAmount><NewInsuranceAmount>50.00</NewInsuranceAmount><NewProcessingFee>100.00</NewProcessingFee><NewInterestAmount>500.00</NewInterestAmount><NewPrincipalAmount>4000.00</NewPrincipalAmount><NewTotalAmountPayable>4650.00</NewTotalAmountPayable><OtherCharges>0.00</OtherCharges><NewTenure>15</NewTenure><ProductCode>LN001</ProductCode><DeductionCode>DC001</DeductionCode><FSPReferenceNumber>FSP{{RANDOM}}</FSPReferenceNumber>'
    }
  },

  LOAN_RESTRUCTURE_AFFORDABILITY_RESPONSE: {
    type: 'LOAN_RESTRUCTURE_AFFORDABILITY_RESPONSE',
    displayName: 'Restructure Affordability',
    description: 'Affordability check response for restructuring',
    endpoint: '/messages/request',
    category: 'Restructuring',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_RESTRUCTURE_AFFORDABILITY_RESPONSE',
      MessageDetails: '<DesiredDeductibleAmount>350.00</DesiredDeductibleAmount><TotalInsurance>50.00</TotalInsurance><TotalProcessingFees>100.00</TotalProcessingFees><TotalInterestRateAmount>500.00</TotalInterestRateAmount><OtherCharges>0.00</OtherCharges><NetLoanAmount>4000.00</NetLoanAmount><TotalAmountToPay>4650.00</TotalAmountToPay><Tenure>15</Tenure><EligibleAmount>4000.00</EligibleAmount><MonthlyReturnAmount>350.00</MonthlyReturnAmount>'
    }
  },

  LOAN_RESTRUCTURE_BALANCE_RESPONSE: {
    type: 'LOAN_RESTRUCTURE_BALANCE_RESPONSE',
    displayName: 'Restructure Balance',
    description: 'Provide current balance for restructuring',
    endpoint: '/messages/request',
    category: 'Restructuring',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_RESTRUCTURE_BALANCE_RESPONSE',
      MessageDetails: '<LoanNumber>LOAN{{RANDOM}}</LoanNumber><InstallmentAmount>450.00</InstallmentAmount><OutstandingBalance>4500.00</OutstandingBalance><PrincipalBalance>4000.00</PrincipalBalance><ValidityDate>{{DATE}}</ValidityDate><LastRepaymentDate>{{DATE}}</LastRepaymentDate><MaturityDate>{{DATE}}</MaturityDate>'
    }
  },

  LOAN_RESTRUCTURED_NOTIFICATION: {
    type: 'LOAN_DISBURSEMENT_NOTIFICATION',
    displayName: 'Restructured Success',
    description: 'Notify successful restructuring (reuses LOAN_DISBURSEMENT_NOTIFICATION)',
    endpoint: '/messages/request',
    category: 'Restructuring',
    useGenericEndpoint: true,
    note: 'Documentation shows this reuses LOAN_DISBURSEMENT_NOTIFICATION message type',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_DISBURSEMENT_NOTIFICATION',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><Reason>Loan restructured successfully</Reason><FSPReferenceNumber>FSP{{RANDOM}}</FSPReferenceNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><TotalAmountToPay>4650.00</TotalAmountToPay><DisbursementDate>{{NOW}}</DisbursementDate>'
    }
  },

  LOAN_RESTRUCTURED_FAILURE_NOTIFICATION: {
    type: 'LOAN_DISBURSEMENT_FAILURE_NOTIFICATION',
    displayName: 'Restructured Failure',
    description: 'Notify restructuring failure (reuses LOAN_DISBURSEMENT_FAILURE_NOTIFICATION)',
    endpoint: '/messages/request',
    category: 'Restructuring',
    useGenericEndpoint: true,
    note: 'Documentation shows this reuses LOAN_DISBURSEMENT_FAILURE_NOTIFICATION message type',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_DISBURSEMENT_FAILURE_NOTIFICATION',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><Reason>Restructuring failed - unable to process new terms</Reason>'
    }
  },

  // ==================== CATEGORY 4: Takeover & Top-Up ====================
  
  LOAN_TAKEOVER_BALANCE_RESPONSE: {
    type: 'LOAN_TAKEOVER_BALANCE_RESPONSE',
    displayName: 'Takeover Balance',
    description: 'Provide takeover balance and payment details',
    endpoint: '/messages/request',
    category: 'Takeover & Top-Up',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_TAKEOVER_BALANCE_RESPONSE',
      MessageDetails: '<LoanNumber>LOAN{{RANDOM}}</LoanNumber><FSPCode>FL8090</FSPCode><FSPReferenceNumber>FSP{{RANDOM}}</FSPReferenceNumber><PaymentReferenceNumber>PAY{{RANDOM}}</PaymentReferenceNumber><TotalPayOffAmount>5000.00</TotalPayOffAmount><OutstandingBalance>4800.00</OutstandingBalance><FSPBankAccount>1234567890</FSPBankAccount><FSPBankAccountName>ZE DONE LOANS</FSPBankAccountName><SWIFTCode>ZDLTZ001</SWIFTCode><MNOChannels>MPESA,TIGOPESA</MNOChannels><FinalPaymentDate>{{NOW}}</FinalPaymentDate><LastDeductionDate>{{DATE}}</LastDeductionDate><DeductionEndDate>{{DATE}}</DeductionEndDate>'
    }
  },

  TAKEOVER_DISBURSEMENT_NOTIFICATION: {
    type: 'TAKEOVER_DISBURSEMENT_NOTIFICATION',
    displayName: 'Takeover Disbursement',
    description: 'Notify takeover disbursement completion',
    endpoint: '/messages/request',
    category: 'Takeover & Top-Up',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'TAKEOVER_DISBURSEMENT_NOTIFICATION',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><Reason>Takeover disbursement successful</Reason><FSPReferenceNumber>FSP{{RANDOM}}</FSPReferenceNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><TotalAmountToPay>6000.00</TotalAmountToPay><DisbursementDate>{{NOW}}</DisbursementDate><PaymentAdvice>Funds transferred to previous FSP</PaymentAdvice><PaymentAdviceAttachment>payment_proof.pdf</PaymentAdviceAttachment>'
    }
  },

  LOAN_TOP_UP_BALANCE_RESPONSE: {
    type: 'LOAN_TOP_UP_BALANCE_RESPONSE',
    displayName: 'Top-Up Balance',
    description: 'Provide top-up balance and payoff details',
    endpoint: '/messages/request',
    category: 'Takeover & Top-Up',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_TOP_UP_BALANCE_RESPONSE',
      MessageDetails: '<LoanNumber>LOAN{{RANDOM}}</LoanNumber><FSPReferenceNumber>FSP{{RANDOM}}</FSPReferenceNumber><PaymentReferenceNumber>PAY{{RANDOM}}</PaymentReferenceNumber><TotalPayoffAmount>2000.00</TotalPayoffAmount><OutstandingBalance>1800.00</OutstandingBalance><FinalPaymentDate>{{NOW}}</FinalPaymentDate><LastDeductionDate>{{DATE}}</LastDeductionDate><LastPayDate>{{DATE}}</LastPayDate><EndDate>{{DATE}}</EndDate>'
    }
  },

  // ==================== CATEGORY 5: Account & Validation ====================
  
  ACCOUNT_VALIDATION_RESPONSE: {
    type: 'ACCOUNT_VALIDATION_RESPONSE',
    displayName: 'Account Validation',
    description: 'Response to account validation request',
    endpoint: '/messages/request',
    category: 'Account & Validation',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'ACCOUNT_VALIDATION_RESPONSE',
      MessageDetails: '<Valid>Y</Valid><Reason>Account is active and verified</Reason>'
    }
  },

  DEFAULTER_DETAILS_TO_EMPLOYER: {
    type: 'DEFAULTER_DETAILS_TO_EMPLOYER',
    displayName: 'Defaulter Details',
    description: 'Send defaulter information to employer',
    endpoint: '/messages/request',
    category: 'Account & Validation',
    useGenericEndpoint: true,
    note: 'Note: Uses InstallmentAmount (not InstallationAmount)',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'DEFAULTER_DETAILS_TO_EMPLOYER',
      MessageDetails: '<VoteCode>V001</VoteCode><VoteName>Ministry of Finance</VoteName><CheckNumber>111222333</CheckNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><FirstName>John</FirstName><MiddleName>Michael</MiddleName><LastName>Doe</LastName><InstallmentAmount>450.00</InstallmentAmount><DeductionName>Loan Repayment</DeductionName><DeductionCode>DC001</DeductionCode><OutstandingBalance>2000.00</OutstandingBalance><LastPayDate>{{DATE}}</LastPayDate>'
    }
  },

  // ==================== CATEGORY 6: Product Operations ====================
  
  PRODUCT_DETAIL: {
    type: 'PRODUCT_DETAIL',
    displayName: 'Product Details',
    description: 'Provide complete product information and terms',
    endpoint: '/messages/request',
    category: 'Product Operations',
    useGenericEndpoint: true,
    note: 'Can contain multiple MessageDetails sections',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'PRODUCT_DETAIL',
      MessageDetails: '<DeductionCode>DC001</DeductionCode><ProductCode>LN001</ProductCode><ProductName>Personal Loan</ProductName><ProductDescription>Standard personal loan for employees</ProductDescription><ForExecutive>N</ForExecutive><MinimumTenure>6</MinimumTenure><MaximumTenure>24</MaximumTenure><InterestRate>15.5</InterestRate><ProcessFee>2.0</ProcessFee><Insurance>1.0</Insurance><MaxAmount>50000.00</MaxAmount><MinAmount>1000.00</MinAmount><RepaymentType>MONTHLY</RepaymentType><Currency>TZS</Currency><InsuranceType>LIFE</InsuranceType><ShariaFacility>N</ShariaFacility><TermsCondition><TermsConditionNumber>TC001</TermsConditionNumber><Description>Applicant must be permanent employee</Description><TCEffectiveDate>{{DATE}}</TCEffectiveDate></TermsCondition><TermsCondition><TermsConditionNumber>TC002</TermsConditionNumber><Description>Minimum 6 months service required</Description><TCEffectiveDate>{{DATE}}</TCEffectiveDate></TermsCondition>'
    }
  },

  PRODUCT_DECOMMISSION: {
    type: 'PRODUCT_DECOMMISSION',
    displayName: 'Product Decommission',
    description: 'Notify product retirement/discontinuation',
    endpoint: '/messages/request',
    category: 'Product Operations',
    useGenericEndpoint: true,
    note: 'Can contain multiple MessageDetails for batch decommissioning',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'PRODUCT_DECOMMISSION',
      MessageDetails: '<ProductCode>LN001</ProductCode>'
    }
  },

  // ==================== CATEGORY 7: FSP Operations ====================
  
  FSP_BRANCHES: {
    type: 'FSP_BRANCHES',
    displayName: 'FSP Branches',
    description: 'Provide FSP branch information by district',
    endpoint: '/messages/request',
    category: 'FSP Operations',
    useGenericEndpoint: true,
    note: 'Nested structure with BranchDetail > Branch elements',
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'FSP_BRANCHES',
      MessageDetails: '<BranchDetail><DistrictCode>D001</DistrictCode><Branch><BranchCode>BR001</BranchCode><BranchName>Main Branch - Dar es Salaam</BranchName></Branch><Branch><BranchCode>BR002</BranchCode><BranchName>Kariakoo Branch</BranchName></Branch></BranchDetail><BranchDetail><DistrictCode>D002</DistrictCode><Branch><BranchCode>BR003</BranchCode><BranchName>Arusha Central Branch</BranchName></Branch></BranchDetail>'
    }
  },

  // ==================== CATEGORY 8: Charges & Payments ====================
  
  LOAN_CHARGES_RESPONSE: {
    type: 'LOAN_CHARGES_RESPONSE',
    displayName: 'Loan Charges',
    description: 'Provide detailed loan charge breakdown',
    endpoint: '/messages/request',
    category: 'Charges & Payments',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_CHARGES_RESPONSE',
      MessageDetails: '<DesiredDeductibleAmount>450.00</DesiredDeductibleAmount><TotalInsurance>50.00</TotalInsurance><TotalProcessingFees>100.00</TotalProcessingFees><TotalInterestRateAmount>650.00</TotalInterestRateAmount><OtherCharges>25.00</OtherCharges><NetLoanAmount>5000.00</NetLoanAmount><TotalAmountToPay>5825.00</TotalAmountToPay><Tenure>12</Tenure><EligibleAmount>5000.00</EligibleAmount><MonthlyReturnAmount>485.42</MonthlyReturnAmount>'
    }
  },

  PAYMENT_ACKNOWLEDGMENT_NOTIFICATION: {
    type: 'PAYMENT_ACKNOWLEDGMENT_NOTIFICATION',
    displayName: 'Payment Acknowledgment',
    description: 'Acknowledge payment receipt from ESS',
    endpoint: '/messages/request',
    category: 'Charges & Payments',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'PAYMENT_ACKNOWLEDGMENT_NOTIFICATION',
      MessageDetails: '<ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber><Remarks>Payment received and processed</Remarks><FSPReferenceNumber>FSP{{RANDOM}}</FSPReferenceNumber><LoanNumber>LOAN{{RANDOM}}</LoanNumber><PaymentStatus>CONFIRMED</PaymentStatus>'
    }
  },

  // ==================== CATEGORY 9: General ====================
  
  RESPONSE: {
    type: 'RESPONSE',
    displayName: 'General Response',
    description: 'Generic response message for various requests',
    endpoint: '/messages/request',
    category: 'General',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'RESPONSE',
      MessageDetails: '<ResponseCode>8000</ResponseCode><Description>Request processed successfully</Description>'
    }
  }
};

// Process template to replace placeholders
export const processTemplate = (template) => {
  const templateStr = JSON.stringify(template);
  const processed = templateStr
    .replace(/{{RANDOM}}/g, generateRandomId())
    .replace(/{{NOW}}/g, getCurrentTimestamp())
    .replace(/{{DATE}}/g, getCurrentDate());
  return JSON.parse(processed);
};

// ==========================================================================
// OUTGOING MESSAGE TYPES - Only these 16 can be manually triggered
// Based on ESS specification
// ==========================================================================
export const OUTGOING_MESSAGE_TYPES = {
  RESPONSE: MESSAGE_TEMPLATES.RESPONSE,
  DEFAULTER_DETAILS_TO_EMPLOYER: MESSAGE_TEMPLATES.DEFAULTER_DETAILS_TO_EMPLOYER,
  FSP_BRANCHES: MESSAGE_TEMPLATES.FSP_BRANCHES,
  FULL_LOAN_REPAYMENT_NOTIFICATION: MESSAGE_TEMPLATES.FULL_LOAN_REPAYMENT_NOTIFICATION,
  FULL_LOAN_REPAYMENT_REQUEST: {
    type: 'FULL_LOAN_REPAYMENT_REQUEST',
    displayName: 'Full Repayment Request',
    description: 'Request full loan repayment details',
    endpoint: '/messages/request',
    category: 'Repayment',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'FULL_LOAN_REPAYMENT_REQUEST',
      MessageDetails: '<LoanNumber>LOAN{{RANDOM}}</LoanNumber><CheckNumber>111222333</CheckNumber><ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber>'
    }
  },
  LOAN_DISBURSEMENT_FAILURE_NOTIFICATION: MESSAGE_TEMPLATES.LOAN_DISBURSEMENT_FAILURE_NOTIFICATION,
  LOAN_DISBURSEMENT_NOTIFICATION: MESSAGE_TEMPLATES.LOAN_DISBURSEMENT_NOTIFICATION,
  LOAN_INITIAL_APPROVAL_NOTIFICATION: MESSAGE_TEMPLATES.LOAN_INITIAL_APPROVAL_NOTIFICATION,
  LOAN_LIQUIDATION_NOTIFICATION: MESSAGE_TEMPLATES.LOAN_LIQUIDATION_NOTIFICATION,
  LOAN_RESTRUCTURE_REQUEST_FSP: MESSAGE_TEMPLATES.LOAN_RESTRUCTURE_REQUEST_FSP,
  LOAN_STATUS_REQUEST: {
    type: 'LOAN_STATUS_REQUEST',
    displayName: 'Loan Status Request',
    description: 'Request current loan status from ESS',
    endpoint: '/messages/request',
    category: 'Disbursement & Approval',
    useGenericEndpoint: true,
    template: {
      Sender: 'ZE DONE',
      Receiver: 'ESS_UTUMISHI',
      FSPCode: 'FL8090',
      MessageType: 'LOAN_STATUS_REQUEST',
      MessageDetails: '<LoanNumber>LOAN{{RANDOM}}</LoanNumber><CheckNumber>111222333</CheckNumber><ApplicationNumber>ESS{{RANDOM}}</ApplicationNumber>'
    }
  },
  PARTIAL_LOAN_REPAYMENT_NOTIFICATION: MESSAGE_TEMPLATES.PARTIAL_LOAN_REPAYMENT_NOTIFICATION,
  PAYMENT_ACKNOWLEDGMENT_NOTIFICATION: MESSAGE_TEMPLATES.PAYMENT_ACKNOWLEDGMENT_NOTIFICATION,
  PRODUCT_DECOMMISSION: MESSAGE_TEMPLATES.PRODUCT_DECOMMISSION,
  PRODUCT_DETAIL: MESSAGE_TEMPLATES.PRODUCT_DETAIL,
  TAKEOVER_DISBURSEMENT_NOTIFICATION: MESSAGE_TEMPLATES.TAKEOVER_DISBURSEMENT_NOTIFICATION
};

// Category grouping for organized UI - ONLY OUTGOING MESSAGE TYPES
export const MESSAGE_CATEGORIES = [
  {
    id: 'disbursement-approval',
    name: 'Disbursement & Approval',
    icon: 'CheckCircle',
    color: '#4caf50',
    messages: [
      OUTGOING_MESSAGE_TYPES.LOAN_DISBURSEMENT_NOTIFICATION,
      OUTGOING_MESSAGE_TYPES.LOAN_DISBURSEMENT_FAILURE_NOTIFICATION,
      OUTGOING_MESSAGE_TYPES.LOAN_INITIAL_APPROVAL_NOTIFICATION,
      OUTGOING_MESSAGE_TYPES.LOAN_LIQUIDATION_NOTIFICATION,
      OUTGOING_MESSAGE_TYPES.LOAN_STATUS_REQUEST
    ]
  },
  {
    id: 'repayment',
    name: 'Repayment',
    icon: 'DollarSign',
    color: '#2196f3',
    messages: [
      OUTGOING_MESSAGE_TYPES.FULL_LOAN_REPAYMENT_NOTIFICATION,
      OUTGOING_MESSAGE_TYPES.FULL_LOAN_REPAYMENT_REQUEST,
      OUTGOING_MESSAGE_TYPES.PARTIAL_LOAN_REPAYMENT_NOTIFICATION,
      OUTGOING_MESSAGE_TYPES.PAYMENT_ACKNOWLEDGMENT_NOTIFICATION
    ]
  },
  {
    id: 'restructuring-takeover',
    name: 'Restructuring & Takeover',
    icon: 'RefreshCw',
    color: '#ff9800',
    messages: [
      OUTGOING_MESSAGE_TYPES.LOAN_RESTRUCTURE_REQUEST_FSP,
      OUTGOING_MESSAGE_TYPES.TAKEOVER_DISBURSEMENT_NOTIFICATION
    ]
  },
  {
    id: 'account-validation',
    name: 'Account & Employer',
    icon: 'Shield',
    color: '#f44336',
    messages: [
      OUTGOING_MESSAGE_TYPES.DEFAULTER_DETAILS_TO_EMPLOYER
    ]
  },
  {
    id: 'product-fsp',
    name: 'Product & FSP',
    icon: 'Package',
    color: '#00bcd4',
    messages: [
      OUTGOING_MESSAGE_TYPES.PRODUCT_DETAIL,
      OUTGOING_MESSAGE_TYPES.PRODUCT_DECOMMISSION,
      OUTGOING_MESSAGE_TYPES.FSP_BRANCHES
    ]
  },
  {
    id: 'general',
    name: 'General',
    icon: 'MessageSquare',
    color: '#795548',
    messages: [
      OUTGOING_MESSAGE_TYPES.RESPONSE
    ]
  }
];

// Get all outgoing message types as array (only the 16 that can be manually triggered)
export const getAllMessageTypes = () => {
  return Object.values(OUTGOING_MESSAGE_TYPES);
};

// Get message template by type (check outgoing first, then all templates)
export const getMessageTemplate = (type) => {
  return OUTGOING_MESSAGE_TYPES[type] || MESSAGE_TEMPLATES[type];
};

// Validate message payload
export const validatePayload = (type, payload) => {
  // Basic validation - can be expanded
  if (!payload || typeof payload !== 'object') {
    return { valid: false, error: 'Payload must be a valid object' };
  }
  return { valid: true };
};

export default MESSAGE_TEMPLATES;
